/**
 * ShaderWarmup — every GL program the game draws, compiled at boot instead of on first use.
 *
 * WebGL links on the GPU process; the main thread stalls only when something asks for the result,
 * and three defers that to the first draw of each program. So the first move, the first shadow, the
 * first water and the first hotbar item each cost a frame of hundreds of milliseconds (measured 390 ms
 * on the render stage). `compileAsync` submits the programs and polls the parallel-compile extension
 * without blocking, and the terrain stays gated (TerrainBatch.setDrawGate) until every program is
 * linked, so no real draw can trigger a cold link.
 *
 * THE MANIFEST is the one place that says what the game draws. It is built from the same constructors
 * and the same `configureLayerMesh` as the real objects, so it cannot describe a material the screen
 * does not use; and `assertNoColdCompiles` (dev) reports any program compiled after the warm-up — a
 * define, a layer or a debug variant added without registering it shows up in the console on the
 * frame it first draws.
 *
 * What `compileAsync` cannot reach: the shadow pass creates its own depth materials, so stand-ins
 * with the same program parameters populate three's global program cache under the key the shadow
 * pass will ask for; and a final one-frame render (shadow pass included) draws every program once, so
 * a browser without KHR_parallel_shader_compile takes its stall here rather than on first movement.
 * The warm objects are kept alive for the session: three frees a program when its last material is
 * disposed, and the held-item material is disposed on every hotbar change.
 */

import * as THREE from 'three';
import {
  LAYER_COUNT, TERRAIN_ATTRS, allocAttrArray, bufferAttribute, configureLayerMesh, layerMaterial,
} from '../voxel/LayerConfig.js';
import { setLodLevelAttr } from '../voxel/lodTexScale.js';
import type { TerrainBatch } from '../voxel/TerrainBatch.js';
import { createHeldItemMaterial, getTerrainMaterial, getTransparentDepthMaterial } from '../material/TerrainMaterial.js';
import { createArmSkinMaterial } from '../scene/FirstPersonArm.js';
import { getPostPipeline } from './PostPipeline.js';
import { PlayerRemote } from '../player/playerRemote.js';
import { getRendererRef } from '../quality/QualityManager.js';

export interface ShaderWarmup {
  /** The live scene's own programs (sky, markers, arm) are linked: safe to start the frame loop. */
  sceneReady: Promise<void>;
  /** Everything in the manifest is linked and drawn once: safe to open the terrain gate. */
  allReady: Promise<void>;
}

/** Past this the gate opens regardless — a driver that never reports completion must not hold the world. */
const WARMUP_TIMEOUT_MS = 8000;

/** Held for the session — a disposed material releases its program (see the header). */
let kept: THREE.Object3D[] = [];
let worldTarget: THREE.WebGLRenderTarget | null = null;
let programBaseline = -1;
const reported = new Set<string>();

/** One triangle carrying every terrain attribute, so the stand-in draws are valid and `flatShading` matches. */
function standInGeometry(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  for (const a of TERRAIN_ATTRS) geo.setAttribute(a.name, bufferAttribute(a, allocAttrArray(a, 3)));
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  pos.setXYZ(0, 0, 0, 0); pos.setXYZ(1, 1, 0, 0); pos.setXYZ(2, 0, 0, 1);
  const nrm = geo.getAttribute('normal') as THREE.BufferAttribute;
  for (let i = 0; i < 3; i++) nrm.setXYZ(i, 0, 1, 0);
  geo.setIndex(new THREE.BufferAttribute(new Uint32Array([0, 1, 2]), 1));
  setLodLevelAttr(geo, 1);
  geo.computeBoundingSphere();
  return geo;
}

function batched(geo: THREE.BufferGeometry, material: THREE.Material): THREE.BatchedMesh {
  const mesh = new THREE.BatchedMesh(1, 3, 3, material);
  const id = mesh.addGeometry(geo);
  mesh.addInstance(id);
  mesh.frustumCulled = false;
  return mesh;
}

function plain(geo: THREE.BufferGeometry, material: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(geo, material);
  mesh.frustumCulled = false;
  return mesh;
}

/** What renders into the world's target: terrain in both object shapes, its shadow casters, a player. */
function worldManifest(geo: THREE.BufferGeometry): THREE.Object3D[] {
  const out: THREE.Object3D[] = [];
  for (let layer = 0; layer < LAYER_COUNT; layer++) {
    // The batch (TerrainBatch) and the plain mesh (BuildPreview) are different programs: USE_BATCHING.
    for (const mesh of [batched(geo, layerMaterial(layer)), plain(geo, layerMaterial(layer))]) {
      configureLayerMesh(mesh, layer, 0);
      out.push(mesh);
    }
  }
  // The shadow pass's own depth material: three's shared MeshDepthMaterial, flipped to the back side
  // for a front-sided caster (WebGLShadowMap.shadowSide). Same program key, so the cache is warm.
  const depth = new THREE.MeshDepthMaterial({ side: THREE.BackSide });
  out.push(batched(geo, depth), plain(geo, depth));
  const foliageDepth = getTransparentDepthMaterial();
  out.push(batched(geo, foliageDepth), plain(geo, foliageDepth));
  out.push(new PlayerRemote(0).mesh);
  return out;
}

/** What renders onto the canvas after the world: the first-person arm and the held item. */
function canvasManifest(geo: THREE.BufferGeometry): THREE.Object3D[] {
  return [
    plain(geo, createArmSkinMaterial()),
    plain(geo, createHeldItemMaterial(false)),
    plain(geo, createHeldItemMaterial(true)),
  ];
}

function withTarget<T>(renderer: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget | null, fn: () => T): T {
  const prev = renderer.getRenderTarget();
  renderer.setRenderTarget(target);
  try { return fn(); } finally { renderer.setRenderTarget(prev); }
}

function programCount(renderer: THREE.WebGLRenderer): number {
  return renderer.info.programs?.length ?? 0;
}

/**
 * Compile everything the session will draw. Returns two gates; see ShaderWarmup.
 * `terrain` is created eagerly so its buffers upload during the warm render rather than on the
 * first chunk.
 */
export function startShaderWarmup(
  renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, terrain: TerrainBatch,
): ShaderWarmup {
  const t0 = performance.now();
  // The world renders into the post pipeline's target on every tier. Output colour space is part
  // of the program key (a target reads as linear), so the compile has to see a target bound.
  worldTarget ??= new THREE.WebGLRenderTarget(4, 4, {
    type: THREE.UnsignedByteType, colorSpace: THREE.SRGBColorSpace, depthBuffer: true,
  });
  const world = worldTarget;

  // The live scene first — sky, spawn marker, snap markers, arm — so the frame loop's first frame
  // finds its programs linked. Small set, tens of milliseconds.
  const sceneReady = withTarget(renderer, world, () => renderer.compileAsync(scene, camera)).then(() => undefined);

  const geo = standInGeometry();
  const worldObjects = worldManifest(geo);
  const canvasObjects = canvasManifest(geo);
  // The post passes: fullscreen triangles, into targets except the composite, which draws the canvas.
  const post = getPostPipeline()?.materialsForWarmup();
  if (post) {
    for (const m of post.target) worldObjects.push(plain(geo, m));
    for (const m of post.canvas) canvasObjects.push(plain(geo, m));
  }
  kept = [...worldObjects, ...canvasObjects];

  const rest = async (): Promise<void> => {
    await sceneReady;
    const warm = new THREE.Scene();
    for (const o of worldObjects) warm.add(o);
    await withTarget(renderer, world, () => renderer.compileAsync(warm, camera, scene));
    for (const o of worldObjects) warm.remove(o);
    for (const o of canvasObjects) warm.add(o);
    await withTarget(renderer, null, () => renderer.compileAsync(warm, camera, scene));
    for (const o of canvasObjects) warm.remove(o);

    // ONE REAL FRAME, shadow pass included: the depth programs are created by the shadow pass alone,
    // the batch buffers upload on their first draw, and a driver without parallel compile stalls
    // here rather than on the first move. Into the live scene so the real lights and the real shadow
    // camera are the ones compiled against.
    terrain.ensureBatches();
    terrain.setDrawGate(true);
    for (const o of worldObjects) scene.add(o);
    const prevAuto = renderer.shadowMap.autoUpdate;
    renderer.shadowMap.needsUpdate = true;
    withTarget(renderer, worldTarget, () => renderer.render(scene, camera));
    renderer.shadowMap.autoUpdate = prevAuto;
    for (const o of worldObjects) scene.remove(o);
    warmTextures();
    programBaseline = programCount(renderer);
    console.log(`[ShaderWarmup] ${programBaseline} programs linked in ${(performance.now() - t0).toFixed(0)} ms`);
  };

  const timeout = new Promise<void>((resolve) => setTimeout(() => {
    console.warn(`[ShaderWarmup] not complete after ${WARMUP_TIMEOUT_MS} ms — opening the terrain gate anyway`);
    terrain.setDrawGate(true);
    resolve();
  }, WARMUP_TIMEOUT_MS));
  const allReady = Promise.race([rest(), timeout]).catch((err) => {
    console.warn('[ShaderWarmup] failed — opening the terrain gate anyway', err);
    terrain.setDrawGate(true);
  });
  return { sceneReady, allReady };
}

/** The stand-ins held for the session (see the header) — for the debug panel and tests. */
export function warmObjectsKept(): number {
  return kept.length;
}

/** Upload the terrain atlases now, not on the first draw that samples them. Safe to call repeatedly. */
export function warmTextures(): void {
  const renderer = getRendererRef();
  const tex = getTerrainMaterial().getTextures();
  if (!renderer || !tex) return;
  for (const t of Object.values(tex)) renderer.initTexture(t);
}

/**
 * DEV: a program compiled after the warm-up is one the manifest does not know. Reported once each,
 * with the material it belongs to. Debug views (F7, F8) compile legitimately and will show up here.
 */
export function assertNoColdCompiles(renderer: THREE.WebGLRenderer): void {
  if (!import.meta.env.DEV || programBaseline < 0) return;
  const programs = renderer.info.programs as Array<{ name: string; cacheKey: string }> | null;
  if (!programs || programs.length <= programBaseline) return;
  for (let i = programBaseline; i < programs.length; i++) {
    const p = programs[i];
    if (reported.has(p.cacheKey)) continue;
    reported.add(p.cacheKey);
    console.warn(`[ShaderWarmup] cold compile: ${p.name} — add it to the manifest (render/ShaderWarmup.ts)`);
  }
  programBaseline = programs.length;
}
